// Firestore helpers for Friends and Contacts
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  serverTimestamp,
  Timestamp,
  writeBatch,
} from 'firebase/firestore';
import { db } from '../../firebase/config';

/**
 * Check rate limit for friend requests (5 per day)
 * Counts all requests sent today (regardless of status) to prevent spam
 */
const checkRateLimit = async (userId: string): Promise<void> => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const requestsRef = collection(db, 'friendRequests');
  const todayQ = query(
    requestsRef,
    where('fromUserId', '==', userId),
    where('createdAt', '>=', Timestamp.fromDate(today))
  );
  
  const todaySnapshot = await getDocs(todayQ);
  const todayCount = todaySnapshot.size;
  
  if (todayCount >= 5) {
    throw new Error('Friend request limit reached. You can send up to 5 friend requests per day.');
  }
};

/**
 * Send a friend request
 */
export const sendFriendRequest = async (
  fromUserId: string,
  toUserId: string
): Promise<string> => {
  if (fromUserId === toUserId) {
    throw new Error('Cannot send friend request to yourself');
  }

  // Check rate limit (5 per day)
  await checkRateLimit(fromUserId);

  // Check if request already exists
  const requestsRef = collection(db, 'friendRequests');
  const existingQ = query(
    requestsRef,
    where('fromUserId', '==', fromUserId),
    where('toUserId', '==', toUserId),
    where('status', 'in', ['pending', 'accepted'])
  );
  const existing = await getDocs(existingQ);
  if (!existing.empty) {
    throw new Error('Friend request already exists');
  }

  // Check if already friends
  const pairId = [fromUserId, toUserId].sort().join('_');
  const friendshipRef = doc(db, 'friendships', pairId);
  const friendship = await getDoc(friendshipRef);
  if (friendship.exists()) {
    throw new Error('Already friends');
  }

  // Create friend request
  const requestRef = doc(collection(db, 'friendRequests'));
  await setDoc(requestRef, {
    fromUserId,
    toUserId,
    status: 'pending',
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  // Create notification for the recipient
  try {
    const fromUserDoc = await getDoc(doc(db, 'users', fromUserId));
    const fromUserData = fromUserDoc.data();
    const fromUserName = fromUserData
      ? `${fromUserData.firstName || ''} ${fromUserData.lastName || ''}`.trim() || 'Someone'
      : 'Someone';
    
    const { createFriendRequestNotification } = await import('../notifications');
    await createFriendRequestNotification(toUserId, fromUserId, fromUserName, requestRef.id);
    console.log('✅ Friend request notification created');
  } catch (notifError) {
    console.error('⚠️ Failed to create friend request notification:', notifError);
    // Don't fail the friend request if notification fails
  }

  return requestRef.id;
};

/**
 * Cancel a friend request (by sender)
 */
export const cancelFriendRequest = async (requestId: string): Promise<void> => {
  const requestRef = doc(db, 'friendRequests', requestId);
  await updateDoc(requestRef, {
    status: 'cancelled',
    updatedAt: serverTimestamp(),
  });
};

/**
 * Accept a friend request
 */
export const acceptFriendRequest = async (
  requestId: string,
  userId: string
): Promise<string> => {
  const requestRef = doc(db, 'friendRequests', requestId);
  const requestDoc = await getDoc(requestRef);

  if (!requestDoc.exists()) {
    throw new Error('Friend request not found');
  }

  const requestData = requestDoc.data();
  if (requestData.toUserId !== userId) {
    throw new Error('Not authorized to accept this request');
  }

  if (requestData.status !== 'pending') {
    throw new Error('Friend request is not pending');
  }

  const fromUserId = requestData.fromUserId;
  const toUserId = requestData.toUserId;

  // Create canonical friendship pair
  const pairId = [fromUserId, toUserId].sort().join('_');
  const friendshipRef = doc(db, 'friendships', pairId);

  const batch = writeBatch(db);

  // Create friendship
  batch.set(friendshipRef, {
    a: fromUserId < toUserId ? fromUserId : toUserId,
    b: fromUserId < toUserId ? toUserId : fromUserId,
    since: serverTimestamp(),
  });

  // Update request status
  batch.update(requestRef, {
    status: 'accepted',
    updatedAt: serverTimestamp(),
  });

  // Cancel any reverse pending request
  const reverseQ = query(
    collection(db, 'friendRequests'),
    where('fromUserId', '==', toUserId),
    where('toUserId', '==', fromUserId),
    where('status', '==', 'pending')
  );
  const reverseSnapshot = await getDocs(reverseQ);
  reverseSnapshot.docs.forEach((doc) => {
    batch.update(doc.ref, {
      status: 'cancelled',
      updatedAt: serverTimestamp(),
    });
  });

  await batch.commit();
  return pairId;
};

/**
 * Ignore a friend request
 */
export const ignoreFriendRequest = async (
  requestId: string,
  userId: string
): Promise<void> => {
  const requestRef = doc(db, 'friendRequests', requestId);
  const requestDoc = await getDoc(requestRef);

  if (!requestDoc.exists()) {
    throw new Error('Friend request not found');
  }

  const requestData = requestDoc.data();
  if (requestData.toUserId !== userId) {
    throw new Error('Not authorized to ignore this request');
  }

  await updateDoc(requestRef, {
    status: 'ignored',
    updatedAt: serverTimestamp(),
  });
};

/**
 * Remove a friend
 */
export const removeFriend = async (
  userId: string,
  otherUserId: string
): Promise<void> => {
  const pairId = [userId, otherUserId].sort().join('_');
  const friendshipRef = doc(db, 'friendships', pairId);
  const friendship = await getDoc(friendshipRef);

  if (!friendship.exists()) {
    throw new Error('Friendship not found');
  }

  const batch = writeBatch(db);

  // Delete friendship
  batch.delete(friendshipRef);

  // Cancel any pending requests between these users
  const requestsQ = query(
    collection(db, 'friendRequests'),
    where('fromUserId', 'in', [userId, otherUserId]),
    where('toUserId', 'in', [userId, otherUserId]),
    where('status', '==', 'pending')
  );
  const requestsSnapshot = await getDocs(requestsQ);
  requestsSnapshot.docs.forEach((doc) => {
    batch.update(doc.ref, {
      status: 'cancelled',
      updatedAt: serverTimestamp(),
    });
  });

  await batch.commit();
};

/**
 * Update missed connections when someone checks in
 * This should ideally be a Cloud Function, but we'll do it client-side for now
 */
export const updateMissedConnections = async (
  eventId: string,
  checkedInUserId: string
): Promise<void> => {
  try {
    // Get all checked-in users for this event
    const rsvpsRef = collection(db, 'events', eventId, 'rsvps');
    const checkedInQ = query(rsvpsRef, where('state', '==', 'checked_in'));
    const checkedInSnapshot = await getDocs(checkedInQ);

    const checkedInUserIds = checkedInSnapshot.docs
      .map((doc) => doc.id)
      .filter((uid) => uid !== checkedInUserId);

    if (checkedInUserIds.length === 0) {
      return; // No other checked-in users
    }

    // Update missed connections for both directions
    const batch = writeBatch(db);
    const now = serverTimestamp();

    for (const otherUserId of checkedInUserIds) {
      // Increment for the user who just checked in
      const missedRef1 = doc(
        db,
        'users',
        checkedInUserId,
        'missedConnections',
        otherUserId
      );
      const missedDoc1 = await getDoc(missedRef1);
      const currentCount1 = missedDoc1.exists()
        ? (missedDoc1.data()?.sharedCount as number) || 0
        : 0;

      batch.set(
        missedRef1,
        {
          sharedCount: currentCount1 + 1,
          lastSharedAt: now,
        },
        { merge: true }
      );

      // Increment for the other user
      const missedRef2 = doc(
        db,
        'users',
        otherUserId,
        'missedConnections',
        checkedInUserId
      );
      const missedDoc2 = await getDoc(missedRef2);
      const currentCount2 = missedDoc2.exists()
        ? (missedDoc2.data()?.sharedCount as number) || 0
        : 0;

      batch.set(
        missedRef2,
        {
          sharedCount: currentCount2 + 1,
          lastSharedAt: now,
        },
        { merge: true }
      );
    }

    await batch.commit();
  } catch (error) {
    console.error('Error updating missed connections:', error);
    // Don't throw - this is a background operation
  }
};

